import React, { useEffect, useMemo, useState } from "react";
import {
  X, Plus, Info, ArrowRight, RotateCcw, Sparkles,
  ShieldAlert, Lock, MessageSquareWarning
} from "lucide-react";
import './GuardrailsEditor.css';

// Toggle styled per ai_guardrails.html
function ToggleSwitch({ checked, onChange, size = "md" }) {
  const sizeCls = size === 'sm' ? 'sm' : '';
  return (
    <label className={`switch ${sizeCls}`} onClick={(e) => e.stopPropagation()}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="track"></span>
      <span className="knob"></span>
    </label>
  );
}

function PlacementBadge({ placement }) {
  const labels = { pre: "Pre-input", post: "Post-output" };
  return (
    <div className="gr-card-title-row">
      {placement.map((p) => (
        <span key={p} className={`badge ${p === 'pre' ? 'pre' : 'post'}`}>{labels[p]}</span>
      ))}
    </div>
  );
}

function TagList({ items, onAdd, onRemove, placeholder }) {
  const [draft, setDraft] = useState("");
  const commit = () => {
    const v = draft.trim();
    if (v) { onAdd(v); setDraft(""); }
  };
  return (
    <div>
      <div className="tag-list">
        {items.length === 0 && <span className="tag-empty">No entries yet</span>}
        {items.map((item, i) => (
          <span key={i} className="tag">
            {item}
            <button onClick={() => onRemove(i)}>
              <X size={12} />
            </button>
          </span>
        ))}
      </div>
      <div className="add-row">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && commit()}
          placeholder={placeholder}
          type="text"
        />
        <button className="add-btn" onClick={commit}><Plus size={14} /> Add</button>
      </div>
    </div>
  );
}

function ChipMultiSelect({ options, selected, onToggle }) {
  return (
    <div className="chip-select">
      {options.map((opt) => {
        const active = selected.includes(opt);
        return (
          <button
            key={opt}
            onClick={() => onToggle(opt)}
            className={`chip ${active ? 'active' : ''}`}
          >
            {opt.replace("_", " ")}
          </button>
        );
      })}
    </div>
  );
}

function FieldLabel({ children, hint }) {
  return (
    <div className="field-label">
      <span>{children}</span>
      {hint && (
        <span title={hint}><Info size={12} /></span>
      )}
    </div>
  );
}

// Guardrail metadata and defaults
const CATEGORY_META = {
  safety: {
    label: "Safety & Injection Defense",
    blurb: "Stops attempts to manipulate the agent and screens for dangerous or self-harm-adjacent content.",
    icon: ShieldAlert,
    ringVar: "--rose-200",
    dotVar: "--rose-500",
    softVar: "--rose-50",
  },
  compliance: {
    label: "Compliance & Data Protection",
    blurb: "Protects PII and enforces underwriting- and claims-specific restricted topics.",
    icon: Lock,
    ringVar: "--amber-200",
    dotVar: "--amber-500",
    softVar: "--amber-50",
  },
  quality: {
    label: "Content Quality",
    blurb: "Keeps responses accurate, unbiased, and on-brand for policyholder communication.",
    icon: MessageSquareWarning,
    ringVar: "--teal-200",
    dotVar: "--teal-500",
    softVar: "--teal-50",
  },
};

const DEFAULT_GUARDRAILS = [
  {
    id: "prompt_injection",
    name: "Prompt Injection",
    category: "safety",
    placement: ["pre"],
    enabled: true,
    description: "Detects attempts to override instructions or disable guardrails mid-conversation.",
    type: "tags",
    field: "injection_patterns",
    config: {
      injection_patterns: [
        "disregard previous instructions",
        "ignore compliance policies",
        "override underwriting guidelines",
        "disable guardrails",
        "reveal confidential pricing algorithms",
        "act as a system admin",
      ],
    },
  },
  {
    id: "openai_moderation",
    name: "OpenAI Moderation",
    category: "safety",
    placement: ["post"],
    enabled: true,
    description: "Runs responses through OpenAI's moderation model before they reach the user.",
    type: "moderation",
    config: {
      moderation_model: "omni-moderation-latest",
      raise_for_categories: ["self-harm", "violence", "hate", "sexual", "harassment"],
    },
  },
  {
    id: "harmful_content",
    name: "Harmful Content",
    category: "safety",
    placement: ["pre", "post"],
    enabled: true,
    description: "Flags instructions for fraud, staged accidents, falsified reports, or physical harm.",
    type: "tags",
    field: "harmful_indicators",
    config: {
      harmful_indicators: [
        "how to commit insurance fraud",
        "stage a car accident",
        "fake a medical report",
        "damage property for payout",
      ],
    },
  },
  {
    id: "content_moderation",
    name: "Content Moderation",
    category: "safety",
    placement: ["pre"],
    enabled: false,
    description: "Category-based keyword screen — a lighter-weight backstop alongside OpenAI Moderation.",
    type: "categoryThreshold",
    config: {
      categories: ["violence", "sexual", "hate", "harassment", "self_harm"],
      threshold: 1,
    },
  },
  {
    id: "pii_detection",
    name: "PII Detection",
    category: "compliance",
    placement: ["pre"],
    enabled: true,
    description: "Detects and masks emails, SSNs, cards, phone numbers, plus insurance-specific identifiers.",
    type: "pii",
    config: {
      enable_email_check: true,
      enable_ssn_check: true,
      enable_credit_card_check: true,
      enable_phone_check: true,
      mask_pii: true,
      custom_patterns: [],
    },
  },
  {
    id: "custom_topics",
    name: "Restricted Topics",
    category: "compliance",
    placement: ["pre"],
    enabled: true,
    description: "Blocks org-defined topics — bypassing underwriting, waiving deductibles, pricing internals.",
    type: "topics",
    config: {
      topics_to_block: [
        "bypass underwriting",
        "waive deductible without approval",
        "insider pricing models",
        "actuarial methodology details",
      ],
      use_regex: true,
      case_sensitive: false,
    },
  },
  {
    id: "bias_detection",
    name: "Bias Detection",
    category: "quality",
    placement: ["pre", "post"],
    enabled: true,
    description: "Heuristic check for stereotyped or discriminatory phrasing in claims and underwriting language.",
    type: "tags",
    field: "bias_indicators",
    config: {
      bias_indicators: ["always deny claims for", "charge women more", "older drivers are always worse"],
    },
  },
  {
    id: "misinformation",
    name: "Misinformation",
    category: "quality",
    placement: ["pre", "post"],
    enabled: false,
    description: "Flags unverified claims or rumor-sourced statements not backed by actuarial data.",
    type: "tags",
    field: "misinformation_indicators",
    config: {
      misinformation_indicators: ["unverified report claims", "viral post says", "rumor is that"],
    },
  },
  {
    id: "profanity_filter",
    name: "Profanity Filter",
    category: "quality",
    placement: ["pre", "post"],
    enabled: true,
    description: "Screens for profanity in either direction, tuned by severity.",
    type: "profanity",
    config: {
      profanity_words: ["bloody", "bollocks"],
      severity_level: "medium",
    },
  },
];

function ConfigBody({ g, updateConfig }) {
  const c = g.config;

  if (g.type === "tags") {
    return (
      <div>
        <FieldLabel hint="Case-insensitive substring match against the message.">Trigger phrases</FieldLabel>
        <TagList
          items={c[g.field]}
          placeholder="Add a phrase or pattern…"
          onAdd={(v) => updateConfig({ [g.field]: [...c[g.field], v] })}
          onRemove={(i) => updateConfig({ [g.field]: c[g.field].filter((_, idx) => idx !== i) })}
        />
      </div>
    );
  }

  if (g.type === "pii") {
    const checks = [
      ["enable_email_check", "Email addresses"],
      ["enable_ssn_check", "SSN / national ID"],
      ["enable_credit_card_check", "Credit card numbers"],
      ["enable_phone_check", "Phone numbers"],
    ];
    return (
      <div>
        <div>
          <FieldLabel>Standard PII checks</FieldLabel>
          <div className="check-grid">
            {checks.map(([key, label]) => (
              <label key={key} className="check-row">
                {label}
                <ToggleSwitch size="sm" checked={c[key]} onChange={(v) => updateConfig({ [key]: v })} />
              </label>
            ))}
          </div>
        </div>
        <label className="toggle-row">
          Mask detected PII instead of blocking
          <ToggleSwitch size="sm" checked={c.mask_pii} onChange={(v) => updateConfig({ mask_pii: v })} />
        </label>
        <div>
          <FieldLabel hint="Insurance-specific identifiers matched by regex — e.g. policy or claim numbers.">
            Custom identifiers
          </FieldLabel>
          <div>
            {c.custom_patterns.map((p, i) => (
              <div key={i} className="kv-row">
                <input
                  value={p.name}
                  onChange={(e) => {
                    const next = [...c.custom_patterns];
                    next[i] = { ...next[i], name: e.target.value };
                    updateConfig({ custom_patterns: next });
                  }}
                  className="name"
                />
                <input
                  value={p.pattern}
                  onChange={(e) => {
                    const next = [...c.custom_patterns];
                    next[i] = { ...next[i], pattern: e.target.value };
                    updateConfig({ custom_patterns: next });
                  }}
                  className="pattern"
                />
                <button
                  onClick={() => updateConfig({ custom_patterns: c.custom_patterns.filter((_, idx) => idx !== i) })}
                  className="remove-kv"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            <button
              onClick={() => updateConfig({ custom_patterns: [...c.custom_patterns, { name: "", pattern: "" }] })}
              className="add-btn"
            >
              <Plus size={14} /> Add identifier
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (g.type === "topics") {
    return (
      <div className="space-y-4">
        <div>
          <FieldLabel hint="Whole conversation topics to block outright, independent of phrasing.">Blocked topics</FieldLabel>
          <TagList
            items={c.topics_to_block}
            placeholder="e.g. bypass underwriting…"
            onAdd={(v) => updateConfig({ topics_to_block: [...c.topics_to_block, v] })}
            onRemove={(i) => updateConfig({ topics_to_block: c.topics_to_block.filter((_, idx) => idx !== i) })}
          />
        </div>
        <div className="flex gap-4">
          <label className="flex-1 flex items-center justify-between text-sm text-slate-600 bg-white border border-slate-200 rounded-md px-3 py-2">
            Treat entries as regex
            <ToggleSwitch size="sm" checked={c.use_regex} onChange={(v) => updateConfig({ use_regex: v })} />
          </label>
          <label className="flex-1 flex items-center justify-between text-sm text-slate-600 bg-white border border-slate-200 rounded-md px-3 py-2">
            Case sensitive
            <ToggleSwitch size="sm" checked={c.case_sensitive} onChange={(v) => updateConfig({ case_sensitive: v })} />
          </label>
        </div>
      </div>
    );
  }

  if (g.type === "moderation") {
    return (
      <div className="space-y-4">
        <div>
          <FieldLabel>Moderation model</FieldLabel>
          <select
            value={c.moderation_model}
            onChange={(e) => updateConfig({ moderation_model: e.target.value })}
            className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <option value="omni-moderation-latest">omni-moderation-latest</option>
            <option value="text-moderation-stable">text-moderation-stable</option>
          </select>
        </div>
        <div>
          <FieldLabel>Categories that trigger a block</FieldLabel>
          <ChipMultiSelect
            options={["self-harm", "violence", "hate", "sexual", "harassment"]}
            selected={c.raise_for_categories}
            onToggle={(opt) =>
              updateConfig({
                raise_for_categories: c.raise_for_categories.includes(opt)
                  ? c.raise_for_categories.filter((x) => x !== opt)
                  : [...c.raise_for_categories, opt],
              })
            }
          />
        </div>
      </div>
    );
  }

  if (g.type === "categoryThreshold") {
    return (
      <div>
        <div>
          <FieldLabel>Active categories</FieldLabel>
          <ChipMultiSelect
            options={["violence", "sexual", "hate", "harassment", "self_harm"]}
            selected={c.categories}
            onToggle={(opt) =>
              updateConfig({
                categories: c.categories.includes(opt)
                  ? c.categories.filter((x) => x !== opt)
                  : [...c.categories, opt],
              })
            }
          />
        </div>
        <div style={{ width: 160 }}>
          <FieldLabel hint="Number of keyword matches required before this guardrail triggers.">Match threshold</FieldLabel>
          <input
            type="number"
            min={1}
            value={c.threshold}
            onChange={(e) => updateConfig({ threshold: Number(e.target.value) })}
          />
        </div>
      </div>
    );
  }

  if (g.type === "profanity") {
    return (
      <div>
        <div>
          <FieldLabel>Additional words</FieldLabel>
          <TagList
            items={c.profanity_words}
            placeholder="Add a word…"
            onAdd={(v) => updateConfig({ profanity_words: [...c.profanity_words, v] })}
            onRemove={(i) => updateConfig({ profanity_words: c.profanity_words.filter((_, idx) => idx !== i) })}
          />
        </div>
        <div style={{ width: 192 }}>
          <FieldLabel>Severity</FieldLabel>
          <div className="severity-toggle">
            {["low", "medium", "high"].map((lvl) => (
              <button
                key={lvl}
                onClick={() => updateConfig({ severity_level: lvl })}
                className={c.severity_level === lvl ? 'active' : ''}
              >
                {lvl}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return null;
}

function GuardrailCard({ g, expanded, onToggleExpand, onToggleEnabled, updateConfig }) {
  return (
    <div className={`gr-card ${g.enabled ? '' : 'disabled'}`}>
      <button onClick={onToggleExpand} className="gr-card-head">
        <span className="chevron">{expanded ? '▾' : '▸'}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="gr-card-title-row">
            <span className={`gr-name ${g.enabled ? '' : 'dim'}`}>{g.name}</span>
            <PlacementBadge placement={g.placement} />
          </div>
          <div className={`gr-desc ${g.enabled ? '' : 'dim'}`}>{g.description}</div>
        </div>
        <span onClick={(e)=>e.stopPropagation()}><ToggleSwitch checked={g.enabled} onChange={onToggleEnabled} /></span>
      </button>
      <div className="gr-card-body" hidden={!expanded}>
        <div style={{ paddingTop: 12 }}>
          <ConfigBody g={g} updateConfig={updateConfig} />
        </div>
      </div>
    </div>
  );
}

function CategorySection({ meta, guardrails, sectionOpen, onToggleSection, expandedIds, onToggleExpand, onToggleEnabled, updateConfig }) {
  const Icon = meta.icon;
  const enabledCount = guardrails.filter((g) => g.enabled).length;
  return (
    <div className="category" style={{ ['--ring']: `var(${meta.ringVar})` }}>
      <button onClick={onToggleSection} className="cat-header" style={{ ['--soft']: `var(${meta.softVar})` }}>
        <span className="cat-dot" style={{ background: `var(${meta.dotVar})` }} />
        {Icon && <Icon size={16} className="cat-icon" />}
        <div className="cat-info">
          <div className="cat-name">{meta.label}</div>
          <div className="cat-blurb">{meta.blurb}</div>
        </div>
        <span className="cat-count">{enabledCount}/{guardrails.length} on</span>
        <span className="chevron">{sectionOpen ? '▾' : '▸'}</span>
      </button>
      <div className="cat-body" hidden={!sectionOpen}>
        {guardrails.map((g) => (
          <GuardrailCard
            key={g.id}
            g={g}
            expanded={expandedIds.has(g.id)}
            onToggleExpand={() => onToggleExpand(g.id)}
            onToggleEnabled={(v) => onToggleEnabled(g.id, v)}
            updateConfig={(patch) => updateConfig(g.id, patch)}
          />
        ))}
      </div>
    </div>
  );
}

function PipelineStrip({ guardrails }) {
  const pre = guardrails.filter((g) => g.enabled && g.placement.includes("pre"));
  const post = guardrails.filter((g) => g.enabled && g.placement.includes("post"));

  return (
    <div className="pipeline">
      <div className="pipeline-title"><Sparkles size={14} /> <span>Where these run in the agent's turn</span></div>
      <div className="pipeline-row">
        <div className="pipe-node">
          <div className="pipe-circle">In</div>
          <div className="pn-label">User input</div>
        </div>
        <ArrowRight size={16} className="pipe-arrow" />
        <div className="pipe-stage">
          <div className="pipe-stage-label">Pre-input</div>
          <div className="pipe-stage-box">
            {pre.length === 0 ? (
              <span className="pipe-empty">No checks enabled</span>
            ) : (
              pre.map((g) => <span key={g.id} className="pipe-chip">{g.name}</span>)
            )}
          </div>
        </div>
        <ArrowRight size={16} className="pipe-arrow" />
        <div className="pipe-node">
          <div className="pipe-circle llm">LLM</div>
          <div className="pn-label">Agent / tools</div>
        </div>
        <ArrowRight size={16} className="pipe-arrow" />
        <div className="pipe-stage">
          <div className="pipe-stage-label">Post-output</div>
          <div className="pipe-stage-box post">
            {post.length === 0 ? (
              <span className="pipe-empty">No checks enabled</span>
            ) : (
              post.map((g) => <span key={g.id} className="pipe-chip">{g.name}</span>)
            )}
          </div>
        </div>
        <ArrowRight size={16} className="pipe-arrow" />
        <div className="pipe-node">
          <div className="pipe-circle">Out</div>
          <div className="pn-label">Response</div>
        </div>
      </div>
    </div>
  );
}

// Transform helpers: UI <-> ComponentGuardrailConfig[]
function arrayPatternsToDict(arr) {
  const out = {};
  (arr || []).forEach(({ name, pattern }) => {
    if (name && typeof pattern === "string") out[name] = pattern;
  });
  return out;
}

function dictToArrayPatterns(dict) {
  if (!dict || typeof dict !== "object") return [];
  return Object.entries(dict).map(([name, pattern]) => ({ name, pattern }));
}

function buildApiListFromUi(guards) {
  return guards
    .filter((g) => g.enabled)
    .map((g) => {
      let cfg = g.config;
      if (g.id === "pii_detection") {
        cfg = { ...cfg, custom_patterns: arrayPatternsToDict(cfg.custom_patterns) };
      }
      return {
        type: g.id,
        default: false, // using custom per-agent config by default
        config: cfg,
      };
    });
}

function hydrateUiFromApi(apiList) {
  // Start from defaults and overlay
  const initial = DEFAULT_GUARDRAILS.map((g) => ({ ...g, config: JSON.parse(JSON.stringify(g.config)) }));
  // Backward-compat: support older 'name' field while migrating to 'type'
  const map = new Map((apiList || []).map((c) => [(c.type || c.name), c]));
  return initial.map((g) => {
    const api = map.get(g.id);
    if (!api) return { ...g, enabled: false };
    const out = { ...g, enabled: typeof api.enabled === 'boolean' ? api.enabled : true };
    if (api.config) {
      out.config = { ...out.config, ...api.config };
      if (g.id === "pii_detection" && api.config.custom_patterns && !Array.isArray(api.config.custom_patterns)) {
        out.config.custom_patterns = dictToArrayPatterns(api.config.custom_patterns);
      }
    }
    return out;
  });
}

export default function GuardrailsEditor({ aiGuardrails = [], onChange, allowedTypes = null }) {
  const [guardrails, setGuardrails] = useState(() => hydrateUiFromApi(aiGuardrails));
  const [expandedIds, setExpandedIds] = useState(new Set(["pii_detection"]));
  const [openSections, setOpenSections] = useState({ safety: true, compliance: true, quality: true });

  // Keep external value -> local sync on prop change
  useEffect(() => {
    setGuardrails(hydrateUiFromApi(aiGuardrails));
  }, [JSON.stringify(aiGuardrails)]);

  const visibleGuardrails = useMemo(() =>
    guardrails.filter((g) => !Array.isArray(allowedTypes) || allowedTypes.includes(g.id))
  , [guardrails, JSON.stringify(allowedTypes)]);

  const enabledCount = useMemo(() => visibleGuardrails.filter((g) => g.enabled).length, [visibleGuardrails]);

  const emit = (next) => {
    setGuardrails(next);
    if (typeof onChange === "function") {
      const onlyVisible = next.filter((g) => !Array.isArray(allowedTypes) || allowedTypes.includes(g.id));
      onChange(buildApiListFromUi(onlyVisible));
    }
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleEnabled = (id, val) => {
    const next = guardrails.map((g) => (g.id === id ? { ...g, enabled: val } : g));
    emit(next);
  };

  const updateConfig = (id, patch) => {
    const next = guardrails.map((g) => (g.id === id ? { ...g, config: { ...g.config, ...patch } } : g));
    emit(next);
  };

  const resetDefaults = () => emit(DEFAULT_GUARDRAILS.map((g) => ({ ...g, config: JSON.parse(JSON.stringify(g.config)) })));

  return (
    <div className="ai-gr">
      <div className="gr-toolbar">
        <div>
          <div className="list-title" style={{ marginBottom: 2 }}>Content Guardrails</div>
          <div className="count">{enabledCount} of {visibleGuardrails.length} checks enabled</div>
        </div>
        <button onClick={resetDefaults} className="reset-btn">
          <RotateCcw size={12} /> Reset to recommended
        </button>
      </div>

      <PipelineStrip guardrails={visibleGuardrails} />

      {Object.entries(CATEGORY_META).map(([catId, meta]) => (
        <CategorySection
          key={catId}
          meta={meta}
          guardrails={visibleGuardrails.filter((g) => g.category === catId)}
          sectionOpen={openSections[catId]}
          onToggleSection={() => setOpenSections((p) => ({ ...p, [catId]: !p[catId] }))}
          expandedIds={expandedIds}
          onToggleExpand={toggleExpand}
          onToggleEnabled={toggleEnabled}
          updateConfig={updateConfig}
        />
      ))}
    </div>
  );
}
